import React from 'react';
import { useMediaQuery } from 'react-responsive';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import styles from './DocumentTable.module.scss';
import DocumentIsEmpty from './DocumentIsEmpty';
import DocumentRow from './DocumentRow';
import { DocumentAttachment } from '@spike/client-model';

export interface DocumentTableProps {
    documents: DocumentAttachment[];
    loading: boolean;
    isEmpty: boolean;
    page: number;
    totalPages: number;
    size: number;
    onPageChange: (page: number) => void;
    onSizeChange: (size: number) => void;
    onDownload: (doc: DocumentAttachment) => void;
    onRename: (doc: DocumentAttachment) => void;
    onDelete: (doc: DocumentAttachment) => void;
}

const colSpan = 4;

export const DocumentTable: React.FC<DocumentTableProps> = ({
    documents,
    loading,
    isEmpty,
    page,
    totalPages,
    size,
    onPageChange,
    onSizeChange,
    onDownload,
    onRename,
    onDelete
}) => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });
    const isTabletOrBelow = useMediaQuery({ query: '(max-width:960px)' });
    const showHeader = !isMobile || (!isEmpty && !loading);

    return (
        <div className={styles.root}>
            <Table
                className={styles.table}
                firstColumnFixed={isTabletOrBelow}
                {...(!isEmpty
                    ? {
                          pagination: {
                              page,
                              totalPages,
                              size,
                              isLoading: loading
                          },
                          onPageChange: onPageChange,
                          onSizeChange: (s: number) => {
                              onSizeChange(s);
                              onPageChange(1);
                          }
                      }
                    : { fullHeight: true })}
                fullHeight={true}
            >
                {showHeader && (
                    <TableHeader className={styles.tableHeader}>
                        <TableHeaderCell>Name</TableHeaderCell>
                        <TableHeaderCell>Created On</TableHeaderCell>
                        <TableHeaderCell>Owner</TableHeaderCell>
                        <TableHeaderCell className={styles.actionsCell} />
                    </TableHeader>
                )}
                <TableBody>
                    {loading && (
                        <tr>
                            <td
                                colSpan={colSpan}
                                style={{ textAlign: 'center', padding: '40px' }}
                            >
                                <div className={styles.loadingContainer}>
                                    <Spinner />
                                </div>
                            </td>
                        </tr>
                    )}
                    {!loading && isEmpty && (
                        <tr>
                            <td
                                colSpan={colSpan}
                                className={styles.emptyCell}
                            >
                                <div className={styles.emptyCellContent}>
                                    <DocumentIsEmpty
                                        title="No documents yet"
                                        text="Add your first document."
                                    />
                                </div>
                            </td>
                        </tr>
                    )}
                    {!loading &&
                        !isEmpty &&
                        documents.map(doc => (
                            <DocumentRow
                                key={doc.id}
                                document={doc}
                                onDownload={onDownload}
                                onRename={onRename}
                                onDelete={onDelete}
                            />
                        ))}
                </TableBody>
            </Table>
        </div>
    );
};

export default DocumentTable;
